import React, {
  InputHTMLAttributes,
  PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Text,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import {
  DropZone,
  HiddenInput,
  RemoveFile,
  DropText,
} from "./ImageInputStyles";

const isDropEvent = (event: React.SyntheticEvent): event is React.DragEvent =>
  event.type === "drop";

const preventDefault = (event: React.SyntheticEvent) => event.preventDefault();

interface ImageInputProps {
  onChange: (files: File[]) => void;
  input?: Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "id" | "type" | "ref"
  >;
  box?: object;
  title?: string;
  noConfirm?: boolean;
  confirmText?: string;
}
export const ImageInput = forwardRef<
  HTMLInputElement,
  PropsWithChildren<ImageInputProps>
>(
  (
    {
      onChange,
      children,
      input,
      box,
      title,
      noConfirm = false,
      confirmText = "Verzenden",
    },
    ref
  ) => {
    const [files, setFiles] = useState<File[]>([]);
    const [interacted, setInteracted] = useState(false);
    const labelRef = useRef<HTMLLabelElement>(null);
    const [urlMap] = useState(new Map<File, string>());
    const id = useId();

    useEffect(
      () => () => {
        urlMap.forEach((url) => URL.revokeObjectURL(url));
        urlMap.clear();
      },
      [urlMap]
    );

    const composedInputProps = useMemo<
      Exclude<ImageInputProps["input"], undefined> &
        Required<Pick<InputHTMLAttributes<HTMLInputElement>, "accept">>
    >(
      () => ({
        accept: "image/*",
        multiple: false,
        ...input,
      }),
      [input]
    );

    const validTypes = useMemo(
      () =>
        composedInputProps.accept
          .split(/\s*,\s*/)
          .filter((type) => type.startsWith("image/")),
      [composedInputProps.accept]
    );

    const getValidItems = useCallback(
      <TItemType extends DataTransferItem | File = DataTransferItem | File>(
        items: Iterable<TItemType>
      ): TItemType[] =>
        Array.from(items as Iterable<TItemType>).filter((item) =>
          validTypes.some((t) =>
            t.includes("*")
              ? new RegExp(t.replace("*", "[^/*]+")).test(item.type)
              : validTypes.includes(item.type)
          )
        ),
      [validTypes]
    );

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent) => {
        setInteracted(true);
        labelRef.current?.classList.remove("canDrop", "invalidDrop");

        const inputDOMNode = document.getElementById(id) as HTMLInputElement;

        const fileList = isDropEvent(event)
          ? event.dataTransfer.files
          : inputDOMNode.files;

        if (isDropEvent(event)) event.preventDefault();

        if (!fileList?.length) return;

        const validFiles = getValidItems(fileList);

        if (!input?.multiple) {
          validFiles.splice(1);
        }

        setFiles(validFiles);

        validFiles.forEach((file) => {
          if (!urlMap.has(file)) {
            urlMap.set(file, URL.createObjectURL(file));
          }
        });
      },
      [input?.multiple, id, getValidItems, urlMap]
    );

    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        const validItems = getValidItems(e.dataTransfer.items);

        const valid = validItems.length > 0;
        const invalid = e.dataTransfer.items.length > 0 && !valid;

        labelRef.current?.classList.toggle("canDrop", valid);
        labelRef.current?.classList.toggle("invalidDrop", invalid);
      },
      [getValidItems]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      if (
        !(e.relatedTarget instanceof Node) ||
        !e.currentTarget.contains(e.relatedTarget)
      ) {
        labelRef.current?.classList.remove("canDrop", "invalidDrop");
      }
    }, []);

    useEffect(() => {
      if (noConfirm && interacted) {
        setInteracted(false);
        onChange(files);
      }
    }, [noConfirm, interacted, files, onChange]);

    const confirm = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        onChange(files);
        setFiles([]);
        urlMap.clear();
      },
      [files, onChange, urlMap]
    );

    return (
      <FormControl isRequired={composedInputProps.required}>
        {title && <FormLabel mb={4}>{title}</FormLabel>}
        <DropZone
          ref={labelRef}
          as="label"
          background="neutral200"
          cursor="pointer"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={preventDefault}
          onDrop={handleChange}
          style={{ display: "block" }}
          {...box}
        >
          <HiddenInput
            {...composedInputProps}
            ref={ref}
            id={id}
            onChange={handleChange}
            type="file"
          />
          {(!files?.length || composedInputProps.multiple) && (
            <Button mb={4} mt={4} onClick={() => labelRef.current?.click()}>
              {children}
            </Button>
          )}
          {!!files?.length && (
            <>
              <Flex>
                {Array.from(files, (file) => (
                  <Flex
                    key={`${file.name}`}
                    alignContent="space-between"
                    direction="column"
                    justifyContent="center"
                    pos="relative"
                  >
                    <RemoveFile
                      onClick={(e: React.UIEvent) => {
                        e.stopPropagation();
                        setFiles((current) =>
                          current.filter((f) => f !== file)
                        );
                        const url = urlMap.get(file);
                        if (url) {
                          URL.revokeObjectURL(url);
                          urlMap.delete(file);
                        }
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    />
                    <Image alt={file.name} src={urlMap.get(file)} w="100px" />
                    <Text>{file.name}</Text>
                  </Flex>
                ))}
              </Flex>
              {!noConfirm && (
                <Button marginTop={4} onClick={confirm}>
                  <CheckIcon mr={2} />
                  <Text>{confirmText}</Text>
                </Button>
              )}
            </>
          )}
          <DropText>
            <Text>Zet hier je bestanden neer of klik om te uploaden</Text>
          </DropText>
        </DropZone>
      </FormControl>
    );
  }
);

ImageInput.displayName = "ImageInput";
